import Link from 'next/link'
import styled from 'styled-components'

import { colors } from '../../constants/colors'

export type HomeSectionKind =
  | 'animals'
  | 'car'
  | 'clothes'
  | 'digital'
  | 'home'
  | 'people'
  | 'realEstate'
  | 'special'
  | 'tech'
  | 'tools'

interface LayerPosition {
  height: number
  left: number
  src: string
  top: number
  transform?: string
  width: number
}

interface BubblePosition {
  background: string
  borderRadius: number
  left: number
  size: number
  top: number
  transform?: string
}

interface HomeSectionItemProps {
  kind: HomeSectionKind
  title: string
  to: string
  width?: number
}

interface SectionConfig {
  background: string
  bubble?: BubblePosition
  layers: LayerPosition[]
}

const sectionConfig: Record<HomeSectionKind, SectionConfig> = {
  animals: {
    background: '#fff2e3',
    bubble: {
      background: '#feceab',
      borderRadius: 26,
      left: 5,
      size: 162,
      top: 68,
      transform: 'rotate(-10.74deg)',
    },
    layers: [{ height: 172, left: 9, src: '/images/home-sections/dog.png', top: 47, width: 116 }],
  },
  car: {
    background: '#e2ecff',
    layers: [
      {
        height: 126,
        left: -13,
        src: '/images/home-sections/car-shape.svg',
        top: 59,
        transform: 'rotate(-10.74deg)',
        width: 192,
      },
      { height: 113, left: -7, src: '/images/home-sections/car.png', top: 50, width: 168 },
    ],
  },
  clothes: {
    background: '#ffe2f2',
    bubble: {
      background: '#ffcde6',
      borderRadius: 19,
      left: 26,
      size: 85,
      top: 69,
      transform: 'rotate(-10.74deg)',
    },
    layers: [
      { height: 135, left: -2, src: '/images/home-sections/shirt.png', top: 71, width: 82 },
      { height: 125, left: 57, src: '/images/home-sections/man.png', top: 49, width: 62 },
    ],
  },
  digital: {
    background: '#fad0ff',
    bubble: {
      background: '#fd9ffd',
      borderRadius: 23,
      left: -6,
      size: 130,
      top: 82,
      transform: 'rotate(-10.74deg)',
    },
    layers: [
      {
        height: 130,
        left: 11,
        src: '/images/home-sections/phone.png',
        top: 61,
        transform: 'rotate(7.58deg)',
        width: 130,
      },
    ],
  },
  home: {
    background: '#e3fcff',
    bubble: {
      background: '#abc7fe',
      borderRadius: 94,
      left: -15,
      size: 162,
      top: 52,
      transform: 'rotate(-10.74deg)',
    },
    layers: [
      { height: 168, left: 31, src: '/images/home-sections/chair.png', top: -13, width: 87 },
    ],
  },
  people: {
    background: '#fffbe3',
    bubble: {
      background: '#fef5ab',
      borderRadius: 26,
      left: 12,
      size: 134,
      top: 57,
      transform: 'rotate(-10.74deg)',
    },
    layers: [
      { height: 124, left: -2, src: '/images/home-sections/people.png', top: 44, width: 127 },
    ],
  },
  realEstate: {
    background: '#fff1e0',
    bubble: {
      background: '#ffe6c7',
      borderRadius: 106,
      left: 14,
      size: 225,
      top: 20,
      transform: 'rotate(-10.74deg)',
    },
    layers: [
      {
        height: 136,
        left: 47,
        src: '/images/home-sections/real-estate.png',
        top: 30,
        width: 223,
      },
    ],
  },
  special: {
    background: '#ffefd0',
    bubble: {
      background: '#fdcd9f',
      borderRadius: 23,
      left: 16,
      size: 138,
      top: 71,
      transform: 'rotate(-10.74deg)',
    },
    layers: [
      {
        height: 137,
        left: -6,
        src: '/images/home-sections/bulldozer.png',
        top: 39,
        width: 169,
      },
    ],
  },
  tech: {
    background: '#ffd0d0',
    bubble: {
      background: '#fd9f9f',
      borderRadius: 23,
      left: 18,
      size: 106,
      top: 69,
      transform: 'rotate(-10.74deg)',
    },
    layers: [
      {
        height: 95,
        left: 9,
        src: '/images/home-sections/motorcycle.png',
        top: 60,
        transform: 'rotate(7.75deg)',
        width: 135,
      },
    ],
  },
  tools: {
    background: '#c2fbff',
    bubble: {
      background: '#a3eaf9',
      borderRadius: 72,
      left: 16,
      size: 135,
      top: 57,
      transform: 'rotate(-10.74deg)',
    },
    layers: [
      {
        height: 153,
        left: -23,
        src: '/images/home-sections/tools.png',
        top: 26,
        transform: 'rotate(11.35deg)',
        width: 151,
      },
    ],
  },
}

export function HomeSectionItem({ kind, title, to, width = 128 }: HomeSectionItemProps) {
  const config = sectionConfig[kind]

  return (
    <Card $background={config.background} $width={width} href={to}>
      {config.bubble && (
        <Bubble
          style={{
            background: config.bubble.background,
            borderRadius: config.bubble.borderRadius,
            height: config.bubble.size,
            left: config.bubble.left,
            top: config.bubble.top,
            transform: config.bubble.transform,
            width: config.bubble.size,
          }}
        />
      )}
      {config.layers.map((layer, index) => (
        <ImageLayer
          alt={''}
          key={`${layer.src}-${index}`}
          src={layer.src}
          style={{
            height: layer.height,
            left: layer.left,
            top: layer.top,
            transform: layer.transform,
            width: layer.width,
          }}
        />
      ))}
      <Title>{title}</Title>
    </Card>
  )
}

const Card = styled(Link)<{ $background: string; $width: number }>`
  position: relative;
  flex: 0 0 ${({ $width }) => $width}px;
  width: ${({ $width }) => $width}px;
  height: 168px;
  overflow: hidden;
  border-radius: 12px;
  background: ${({ $background }) => $background};
  color: ${colors.black};
  text-decoration: none;
`

const Title = styled.span`
  position: absolute;
  z-index: 3;
  top: 12px;
  left: 19px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  white-space: pre-line;
`

const Bubble = styled.div`
  position: absolute;
`

const ImageLayer = styled.img`
  position: absolute;
  object-fit: cover;
  pointer-events: none;
`
